import {produce} from 'immer'
import type {StateCreator} from 'zustand'
import type {Comment, DiffAnnotation, Thread} from '@github-ui/conversations'

export type Markers = {
  threads: {[id: number]: Thread}
  annotations: {[id: number]: DiffAnnotation}
}

export interface MarkersSliceOpts {
  markers?: Markers | undefined
}

type DeleteThreadCommentResult = 'not_found' | 'comment_deleted' | 'thread_deleted'

type MarkersSliceState = {
  markers?: Markers
}
type MarkersSliceActions = {
  updateThread: (id: number, transform: (old: Thread | undefined) => Thread | undefined) => void
  updateThreads: (transform: (old: Record<number, Thread>) => Record<number, Thread>) => void
  deleteThreadComment: (threadID: number, commentID: string) => DeleteThreadCommentResult
  updateThreadComment: (args: {
    threadID: number
    match: (comment: Comment) => boolean
    transform: (old: Comment | undefined) => Comment | undefined
  }) => void
}
export type MarkersSlice = MarkersSliceState & MarkersSliceActions

export const createMarkersSlice =
  ({markers}: MarkersSliceOpts): StateCreator<MarkersSlice, [['zustand/devtools', never]], [], MarkersSlice> =>
  (set, get) => ({
    markers,
    updateThread: (id, transform) => {
      const oldMarkers = get().markers
      if (!oldMarkers) {
        return
      }

      const oldThread = oldMarkers.threads[id]
      const newThread = transform(oldThread)
      if (!newThread) {
        return
      }

      // TODO: use immer?
      set(
        {
          markers: {
            ...oldMarkers,
            threads: {
              ...oldMarkers.threads,
              [id]: newThread,
            },
          },
        },
        undefined,
        'updateThread',
      )
    },
    updateThreads: transform => {
      const oldMarkers = get().markers
      if (!oldMarkers) {
        return
      }

      set({
        markers: {
          ...oldMarkers,
          threads: transform(oldMarkers.threads),
        },
      })
    },
    deleteThreadComment: (threadID, commentID) => {
      let result: DeleteThreadCommentResult = 'not_found'
      const oldMarkers = get().markers
      if (!oldMarkers) {
        return result
      }

      set(
        {
          markers: produce(oldMarkers, m => {
            const thread = m.threads[threadID]
            if (!thread) {
              result = 'not_found'
              return
            }

            const commentsData = thread.commentsData
            const filteredComments = commentsData.comments.filter(c => c.id !== commentID)

            if (commentsData.comments.length === filteredComments.length) {
              result = 'not_found'
              return
            }

            if (filteredComments.length === 0) {
              result = 'thread_deleted'
              delete m.threads[threadID]
            } else {
              result = 'comment_deleted'
              commentsData.comments = filteredComments
            }
          }),
        },
        undefined,
        'deleteThreadComment',
      )

      return result
    },
    updateThreadComment: ({threadID, match, transform}) => {
      const oldMarkers = get().markers
      if (!oldMarkers) {
        return
      }

      set(
        {
          markers: produce(oldMarkers, m => {
            const thread = m.threads[threadID]
            if (!thread) {
              return
            }
            const commentsData = thread.commentsData

            const index = commentsData.comments.findIndex(match)
            const oldComment = commentsData.comments[index]
            const newComment = transform(oldComment)
            if (!newComment) {
              return
            }

            if (index === -1) {
              commentsData.comments.push(newComment)
            } else {
              commentsData.comments[index] = newComment
            }
          }),
        },
        undefined,
        'updateThreadComment',
      )
    },
  })
